import React, {type ChangeEvent, type MouseEvent, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {getMessageFromError} from "@/global/helpers/getMessageFromError";
import {Alert, CircularProgress, type SelectChangeEvent, Stack} from "@mui/material";
import {BackLink} from "@/global/components/BackLink";
import {useQueryClient} from "@tanstack/react-query";
import {useSearchParams} from "react-router-dom";
import {IinApplicationStep} from "./IinApplicationStep";
import {ApplicationRegisterForm} from "@/modules/exam-applications/pages/register/components";
import {
    apiCreateOnlineAitestTransaction,
    apiStartPddTestExam,
    apiStudentAiTestByIin,
    type ApplicationCreateDtoCategoryEnum
} from "@/modules/exam-applications/shared";
import type {StudentDto} from "@/global/api/gen/ts/StudentDto";
import {
    StartTransactionStep
} from "@/modules/exam-applications/pages/register/components/ApplicationExamStepper/StartTransactionStep";
import {
    apiCreateQuickTransaction,
    apiTransactionFreedomWebhook
} from "@/modules/transactions/services/transactions.services";
import type {TransactionCreateDtoPaymentSystemEnum} from "@/global/api/gen/ts/TransactionCreateDto";
import {TransactionWaitingStep} from "@/global/components/TransactionWaitingStep";
import FlexHeader from "@/global/components/FlexHeader";
import {openPage} from "@/global/helpers/openPage";
import {SchoolPickStep} from "./SchoolPickStep";
import {VariantPickStep} from "./VariantPickStep";
import {ApplicationRegisterOnlineForm} from "./ApplicationRegisterOnlineForm";
import {LoadingButton} from "@mui/lab";

type Steps =
    'iin'
    | 'startTransaction'
    | 'transaction'
    | 'school'
    | 'variant'
    | 'formOffline'
    | 'formOnline'
    | 'onlineEnd'

type Variant = 'online' | 'offline';

export function ApplicationExamStepper() {
    const [step, setStep] = useState<Steps>('iin');
    const [searchParams, setSearchParams] = useSearchParams();
    const iinSP = searchParams.get('iin');
    const [iin, setIin] = useState<string>(iinSP ?? '');
    const [variant, setVariant] = useState<Variant>('online');
    const [selectedStudent, setSelectedStudent] = useState<StudentDto | null>(null);
    const [stop, setStop] = useState<boolean>(false);

    const {data: studentInfo, isPending: studentInfoLoading, mutate} = apiStudentAiTestByIin();

    const queryClient = useQueryClient();

    const {
        mutate: mutateTransaction,
        isPending: isPendingTransaction,
        data: dataMutateTransaction,
        reset: resetMutateTransaction,
    } = apiCreateQuickTransaction();

    const {
        mutate: mutateOnlineApplication,
        isPending: isPendingOnlineApplication,
        data: dataMutateOnlineApplication,
        reset: resetOnlineApplication
    } = apiCreateOnlineAitestTransaction();

    const {
        mutate: mutateStartPddExam,
        isPending: isPendingStartPddExam
    } = apiStartPddTestExam();

    const {
        data: dataTransaction,
        refetch: refetchCertificateTransaction
    } = apiTransactionFreedomWebhook(dataMutateTransaction?.invoice_id || dataMutateOnlineApplication?.transaction?.invoice_id, stop);

    useEffect(() => {
        if (dataTransaction && dataTransaction.status === 2) {
            setStop(true);
            if (variant === 'online') {
                setStep('onlineEnd');
                return;
            }
            setStep('formOffline');
        }
        if (dataTransaction && (dataTransaction.status === 3 || dataTransaction.status === 10)) {
            setStep('iin');
            toast.error('Ошибка транзакции. Повторите позже.');
            return;
        }
    }, [dataTransaction]);

    useEffect(() => {
        if (iinSP) {
            handleFetchStudents();
            searchParams.delete('iin');
            setSearchParams(searchParams, {
                replace: true
            });
        }
    }, [iinSP]);

    const handleFetchStudents = () => {
        mutate(iin, {
            onSuccess: (data) => {
                if (data.onlineApplication) {
                    setStep('onlineEnd');
                    return;
                }
                setStep('school');
            },
            onError: (error) => {
                toast.error(`Ошибка при получение школ: ${getMessageFromError(error)}`);
                setIin('');
                return;
            }
        });
    };

    const handleIinChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const sendIin = e.currentTarget.value.replace(/\D/g, '');
        if (sendIin.length > 12) {
            return;
        }
        setIin(sendIin);
    };

    const returnFirstStep = async () => {
        setStep('iin');
        setIin('');
        setSelectedStudent(null);
        setStop(false);
        await queryClient.cancelQueries();
        resetOnlineApplication();
        resetMutateTransaction();
    };

    const handleStudentSelect = (event: SelectChangeEvent) => {
        const selectedId = +(event.target.value);
        const student = studentInfo?.students.find((s: StudentDto) => s.id === selectedId) || null;
        setSelectedStudent(student);
    };

    const handlePickStudent = () => {
        if (!selectedStudent) {
            toast.error('Выберите школу');
            return;
        }
        if (!studentInfo) {
            toast.error('Ошибка при получение данных по ИИН');
            void returnFirstStep();
            return;
        }
        setStep('variant');
    };

    const handlePickVariant = () => {
        if (variant === 'online') {
            setStep('formOnline');
            return;
        }
        if (studentInfo?.isNeedPaid) {
            setStep('startTransaction');
            return;
        }
        setStep('formOffline');
    };

    const startPddExam = () => {
        if (!dataMutateOnlineApplication && !(studentInfo && studentInfo.onlineApplication)) {
            toast.error('Нет оплаченных заявок на сдачу');
            return;
        }
        mutateStartPddExam({
            id: (dataMutateOnlineApplication?.id ?? studentInfo?.onlineApplication?.id)!
        }, {
            onSuccess: (data) => {
                openPage(data.url);
            },
            onError: (error) => {
                toast.error(`Ошибка при создание теста: ${getMessageFromError(error)}`);
            }
        });
    };

    const handleStartTransaction = (e: MouseEvent<HTMLButtonElement>) => {
        const system = e.currentTarget.getAttribute('data-id');
        if (!system) {
            toast.error('Ошибка попробуйте еще раз позже. Если ошибка повторяется, сообщите в поддержку');
            return;
        }
        mutateTransaction({
            payment_system: system as TransactionCreateDtoPaymentSystemEnum,
            type: 'AITest',
            amount: 1,
            iin,
        }, {
            onSuccess: (data) => {
                if (data && data.payment_system === 'kaspi' && data.payment_url) {
                    openPage(data.payment_url);
                }
                if (data && data.payment_system === 'kaspi' && !data.payment_url) {
                    toast.error('Ошибка при создание ссылки для оплаты в Kaspi.kz. Повторить позже');
                    void returnFirstStep();
                    return;
                }
                setStep('transaction');
            }
        });
    };

    const handleStartOnlineTransaction = (e: MouseEvent<HTMLButtonElement>, email: string, category: ApplicationCreateDtoCategoryEnum) => {
        const system = e.currentTarget.getAttribute('data-id');
        if (!system) {
            toast.error('Ошибка попробуйте еще раз позже. Если ошибка повторяется, сообщите в поддержку');
            return;
        }
        if (!selectedStudent) {
            toast.error('Ошибка. Студент не выбран. Вернитесь к первому шагу.');
            return;
        }
        mutateOnlineApplication({
            payment_system: system as TransactionCreateDtoPaymentSystemEnum,
            type: 'pdd_test_exam',
            amount: 1,
            iin,
            category: category,
            studentId: selectedStudent?.id,
            email: email
        }, {
            onSuccess: (data) => {
                if (data && data.transaction?.payment_system === 'kaspi' && data.transaction?.payment_url) {
                    openPage(data.transaction.payment_url);
                }
                if (data && data?.transaction?.payment_system === 'kaspi' && !data.transaction.payment_url) {
                    toast.error('Ошибка при создание ссылки для оплаты в Kaspi.kz. Повторить позже');
                    void returnFirstStep();
                    return;
                }
                setStep('transaction');
            }
        });
    };

    const handleFetchTransaction = async () => {
        const res = await refetchCertificateTransaction();
        if (res.data && res.data.status !== 2) {
            toast.warn('Статус оплаты - не оплачен. Ожидайте обновления.');
        }
    };

    if (step === 'iin') {
        return (
            <IinApplicationStep
                iin={iin}
                handleIinChange={handleIinChange}
                handleFetchStudents={handleFetchStudents}
                studentsByIINLoading={studentInfoLoading}
            />
        );
    }

    if (step === 'variant') {
        return (
            <VariantPickStep
                value={variant}
                returnFirstStep={returnFirstStep}
                handleChangeVariant={(value) => setVariant(value)}
                handlePickVariant={handlePickVariant}
            />
        );
    }

    if (step === 'startTransaction') {
        return (
            <StartTransactionStep
                loadingStartTransaction={isPendingTransaction}
                handleStartTransaction={handleStartTransaction}
                returnFirstStep={returnFirstStep}
            />
        );
    }

    if (step === 'transaction') {
        if (!dataMutateTransaction && !dataMutateOnlineApplication) {
            return (
                <FlexHeader>
                    <CircularProgress/>
                </FlexHeader>
            );
        }

        return (
            <TransactionWaitingStep
                returnFirstStep={returnFirstStep}
                transaction={(dataMutateTransaction ?? dataMutateOnlineApplication?.transaction)!}
                handleFetchTransaction={handleFetchTransaction}
            />
        );
    }

    if (step === 'school' && studentInfo) {
        return (
            <SchoolPickStep
                returnFirstStep={returnFirstStep}
                handlePickStudent={handlePickStudent}
                handleStudentSelect={handleStudentSelect}
                studentInfo={studentInfo}
            />
        );
    }

    if (step === 'formOnline' && selectedStudent) {
        return (
            <ApplicationRegisterOnlineForm
                handleStartOnlineTransaction={handleStartOnlineTransaction}
                returnFirstStep={returnFirstStep}
                isPending={isPendingOnlineApplication}
            />
        );
    }

    if (step === 'formOffline' && selectedStudent) {
        return (
            <ApplicationRegisterForm
                returnFirstStep={returnFirstStep}
                studentInfo={selectedStudent}
            />
        );
    }

    if (step === 'onlineEnd') {
        return (
            <>
                <BackLink
                    label={'Вернуться к вводу ИИН'}
                    onClick={returnFirstStep}
                    sx={{
                        alignSelf: 'flex-start'
                    }}
                />
                <Stack
                    display={'flex'}
                    flexDirection={'column'}
                    sx={{
                        width: {lg: '60%', xs: '100%'},
                        borderRadius: '26px',
                        backgroundColor: '#FFFFFF',
                        padding: 3,
                        gap: '16px'
                    }}
                >
                    <Alert severity={'info'}>
                        Для прохождения произойдет переход на сервис PDDTest.kz
                    </Alert>
                    <LoadingButton
                        loading={isPendingStartPddExam}
                        onClick={startPddExam}
                    >
                        Начать тест
                    </LoadingButton>
                </Stack>
            </>
        );
    }

    return (
        <>
            <BackLink
                label={'Вернуться к вводу ИИН'}
                onClick={returnFirstStep}
                sx={{
                    alignSelf: 'flex-start'
                }}
            />
            <Stack
                display={'flex'}
                flexDirection={'column'}
                sx={{
                    width: {lg: '60%', xs: '100%'},
                    borderRadius: '26px',
                    backgroundColor: '#FFFFFF',
                    padding: 3,
                    gap: '16px'
                }}
            >
                <Alert severity={'error'}>
                    Ошибка при получение подачи заявки. Повторитe, пожалуйста, позже
                </Alert>
            </Stack>
        </>
    );
}